/**
 * Skipper upload flow: presign → S3 PUT → auto-complete.
 * Similar to admin uploadTrackFile() but uses the skipper endpoint
 * which auto-resolves the boat from the authenticated user's entry.
 */

import type { SkipperUploadResponse, CompleteUploadResponse } from '@regatta-replay/types';
import type { UploadState } from './upload';
import { api } from './api';
import { useAuthStore } from '../stores/auth';

export interface SkipperUploadOptions {
  regattaId: string;
  onStateChange: (state: UploadState) => void;
  signal?: AbortSignal;
}

export async function uploadSkipperTrack(
  file: File,
  options: SkipperUploadOptions
): Promise<SkipperUploadResponse> {
  const { regattaId, onStateChange, signal } = options;

  const state: UploadState = {
    file,
    phase: 'presigning',
    progress: 0,
  };
  onStateChange({ ...state });

  // Step 1: Get presigned URL (skipper endpoint auto-resolves boat)
  const presign = await api.post<SkipperUploadResponse>(
    `/regattas/${regattaId}/tracks/upload`,
    {
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      fileSize: file.size,
    }
  );

  state.uploadId = presign.uploadId;
  state.s3Key = presign.s3Key;
  state.phase = 'uploading';
  onStateChange({ ...state });

  // Step 2: Upload to S3 via presigned URL
  await uploadToS3(presign.presignedUrl, file, (progress) => {
    state.progress = progress;
    onStateChange({ ...state });
  }, signal);

  // Step 3: Complete the upload — create the RawTrack record + enqueue the
  // SQS processing message. WITHOUT this call the file sits in S3 unprocessed
  // (no RawTrack, no parser run). Mirrors the admin upload-complete path.
  state.phase = 'processing';
  state.progress = 100;
  onStateChange({ ...state });

  await api.post<CompleteUploadResponse>(
    `/uploads/${presign.uploadId}/complete`,
    {
      regattaId,
      boatId: presign.boatId,
      s3Key: presign.s3Key,
      fileName: file.name,
      fileSize: file.size,
    }
  );

  state.phase = 'complete';
  onStateChange({ ...state });

  return presign;
}

function uploadToS3(
  presignedUrl: string,
  file: File,
  onProgress: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    const token = useAuthStore.getState().token;
    if (token && presignedUrl.startsWith('/')) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(file);
  });
}
